/**
 * Per-category shape profiles that control procedural aircraft proportions.
 * Each aircraft category gets a distinct silhouette.
 */

import type { AircraftCategory } from '@/types/aircraft-types'
import type { CategoryProfile } from './types'

const PROFILES: Record<AircraftCategory, CategoryProfile> = {
  single_engine_piston: {
    fuselageDiameterRatio: 0.65,
    noseConeLengthRatio: 0.12,
    tailTaperRatio: 0.15,
    wingPositionRatio: 0.38,
    wingChordRatio: 0.22,
    wingSweepDeg: 0,
    wingVerticalOffset: 0.6,    // high wing
    wingDihedralDeg: 2,
    hStabSpanRatio: 0.30,
    hStabChordRatio: 0.70,
    vStabHeightFallbackRatio: 0.12,
    tailType: 'conventional',
    engineCount: 1,
    enginePlacement: 'nose',
    engineDiameterRatio: 0.7,
    hasPropDisc: true,
    showGear: true,
  },

  multi_engine_piston: {
    fuselageDiameterRatio: 0.60,
    noseConeLengthRatio: 0.10,
    tailTaperRatio: 0.14,
    wingPositionRatio: 0.40,
    wingChordRatio: 0.20,
    wingSweepDeg: 0,
    wingVerticalOffset: -0.3,   // low wing
    wingDihedralDeg: 3,
    hStabSpanRatio: 0.30,
    hStabChordRatio: 0.65,
    vStabHeightFallbackRatio: 0.13,
    tailType: 'conventional',
    engineCount: 2,
    enginePlacement: 'wing',
    engineDiameterRatio: 0.55,
    hasPropDisc: true,
    showGear: true,
  },

  turboprop: {
    fuselageDiameterRatio: 0.55,
    noseConeLengthRatio: 0.10,
    tailTaperRatio: 0.14,
    wingPositionRatio: 0.42,
    wingChordRatio: 0.18,
    wingSweepDeg: 0,
    wingVerticalOffset: -0.4,   // low wing
    wingDihedralDeg: 4,
    hStabSpanRatio: 0.32,
    hStabChordRatio: 0.65,
    vStabHeightFallbackRatio: 0.14,
    tailType: 'conventional',
    engineCount: 2,
    enginePlacement: 'wing',
    engineDiameterRatio: 0.50,
    hasPropDisc: true,
    showGear: true,
  },

  very_light_jet: {
    fuselageDiameterRatio: 0.55,
    noseConeLengthRatio: 0.12,
    tailTaperRatio: 0.18,
    wingPositionRatio: 0.42,
    wingChordRatio: 0.20,
    wingSweepDeg: 15,
    wingVerticalOffset: -0.5,
    wingDihedralDeg: 3,
    hStabSpanRatio: 0.30,
    hStabChordRatio: 0.60,
    vStabHeightFallbackRatio: 0.15,
    tailType: 't-tail',
    engineCount: 2,
    enginePlacement: 'rear_fuselage',
    engineDiameterRatio: 0.45,
    hasPropDisc: false,
    showGear: true,
  },

  light_jet: {
    fuselageDiameterRatio: 0.50,
    noseConeLengthRatio: 0.12,
    tailTaperRatio: 0.18,
    wingPositionRatio: 0.43,
    wingChordRatio: 0.18,
    wingSweepDeg: 20,
    wingVerticalOffset: -0.5,
    wingDihedralDeg: 3,
    hStabSpanRatio: 0.30,
    hStabChordRatio: 0.60,
    vStabHeightFallbackRatio: 0.16,
    tailType: 't-tail',
    engineCount: 2,
    enginePlacement: 'rear_fuselage',
    engineDiameterRatio: 0.45,
    hasPropDisc: false,
    showGear: true,
  },

  midsize_jet: {
    fuselageDiameterRatio: 0.48,
    noseConeLengthRatio: 0.11,
    tailTaperRatio: 0.18,
    wingPositionRatio: 0.44,
    wingChordRatio: 0.17,
    wingSweepDeg: 25,
    wingVerticalOffset: -0.6,
    wingDihedralDeg: 4,
    hStabSpanRatio: 0.28,
    hStabChordRatio: 0.60,
    vStabHeightFallbackRatio: 0.16,
    tailType: 't-tail',
    engineCount: 2,
    enginePlacement: 'rear_fuselage',
    engineDiameterRatio: 0.50,
    hasPropDisc: false,
    showGear: true,
  },

  super_midsize_jet: {
    fuselageDiameterRatio: 0.45,
    noseConeLengthRatio: 0.10,
    tailTaperRatio: 0.18,
    wingPositionRatio: 0.45,
    wingChordRatio: 0.16,
    wingSweepDeg: 28,
    wingVerticalOffset: -0.6,
    wingDihedralDeg: 4,
    hStabSpanRatio: 0.28,
    hStabChordRatio: 0.55,
    vStabHeightFallbackRatio: 0.17,
    tailType: 't-tail',
    engineCount: 2,
    enginePlacement: 'rear_fuselage',
    engineDiameterRatio: 0.50,
    hasPropDisc: false,
    showGear: true,
  },

  large_jet: {
    fuselageDiameterRatio: 0.42,
    noseConeLengthRatio: 0.09,
    tailTaperRatio: 0.16,
    wingPositionRatio: 0.45,
    wingChordRatio: 0.16,
    wingSweepDeg: 30,
    wingVerticalOffset: -0.7,
    wingDihedralDeg: 5,
    hStabSpanRatio: 0.30,
    hStabChordRatio: 0.55,
    vStabHeightFallbackRatio: 0.17,
    tailType: 'conventional',
    engineCount: 2,
    enginePlacement: 'wing',
    engineDiameterRatio: 0.55,
    hasPropDisc: false,
    showGear: true,
  },

  ultra_long_range: {
    fuselageDiameterRatio: 0.40,
    noseConeLengthRatio: 0.08,
    tailTaperRatio: 0.15,
    wingPositionRatio: 0.46,
    wingChordRatio: 0.15,
    wingSweepDeg: 33,
    wingVerticalOffset: -0.7,
    wingDihedralDeg: 5,
    hStabSpanRatio: 0.28,
    hStabChordRatio: 0.55,
    vStabHeightFallbackRatio: 0.18,
    tailType: 'conventional',
    engineCount: 2,
    enginePlacement: 'wing',
    engineDiameterRatio: 0.55,
    hasPropDisc: false,
    showGear: true,
  },

  helicopter: {
    // Helicopter uses a completely separate builder — these values are mostly unused
    fuselageDiameterRatio: 0.80,
    noseConeLengthRatio: 0.15,
    tailTaperRatio: 0.0,
    wingPositionRatio: 0.5,
    wingChordRatio: 0.0,
    wingSweepDeg: 0,
    wingVerticalOffset: 0,
    wingDihedralDeg: 0,
    hStabSpanRatio: 0.0,
    hStabChordRatio: 0.0,
    vStabHeightFallbackRatio: 0.10,
    tailType: 'conventional',
    engineCount: 0,
    enginePlacement: 'none',
    engineDiameterRatio: 0,
    hasPropDisc: false,
    showGear: false,
  },
}

/** Default profile for unknown categories */
const DEFAULT_PROFILE = PROFILES.light_jet

export function getCategoryProfile(category: AircraftCategory | null): CategoryProfile {
  if (!category) return DEFAULT_PROFILE
  return PROFILES[category] ?? DEFAULT_PROFILE
}
